// src/pages/access/AuditLogTab.tsx — Phase 9 c11 (audit log reader).
//
// GET /api/access/audit, paginated. Filters: action prefix (free-text),
// from/to date pickers, include-self-views toggle. Each row shows
// timestamp, actor, action, subject, and a collapsible JSON payload.
//
// No mutations — this is purely a read view.

import { useEffect, useState } from "react";
import { apiFetch, ApiError } from "../../api";
import { C, F } from "../../theme";
import { Button, Card, Eyebrow, Field } from "../../components/ui";

interface AuditActor {
  upn: string | null;
  userId?: number;
  first?: string | null;
  last?: string | null;
}

interface AuditSubject {
  id: number;
  first: string | null;
  last: string | null;
  email: string | null;
}

interface AuditRow {
  id: number;
  ts: string;
  action: string;
  actor: AuditActor;
  subject: AuditSubject | null;
  resourceType: string | null;
  resourceId: string | null;
  payload: unknown;
  ip: string | null;
  isSelfView: boolean;
}

interface AuditResponse {
  rows: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 50;

export function AuditLogTab() {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [includeSelfViews, setIncludeSelfViews] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(PAGE_SIZE));
    if (action.trim()) params.set("action", action.trim());
    if (from.trim()) params.set("from", from.trim());
    if (to.trim()) params.set("to", to.trim());
    if (includeSelfViews) params.set("includeSelfViews", "true");

    apiFetch<AuditResponse>(`/api/access/audit?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows);
        setTotal(res.total);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : "Couldn't load audit log.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, action, from, to, includeSelfViews]);

  function toggle(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Card>
        <Eyebrow>Filters</Eyebrow>
        <div style={{ marginTop: 8 }}>
          <Field
            label="Action (exact)"
            value={action}
            onChange={(v) => {
              setAction(v);
              setPage(1);
            }}
            ph="e.g. pin.reveal, user.block, permission.change"
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field
              label="From"
              value={from}
              onChange={(v) => {
                setFrom(v);
                setPage(1);
              }}
              type="date"
            />
            <Field
              label="To"
              value={to}
              onChange={(v) => {
                setTo(v);
                setPage(1);
              }}
              type="date"
            />
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: F.body,
              fontWeight: 700,
              fontSize: 13,
              color: C.inkSoft,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={includeSelfViews}
              onChange={(e) => {
                setIncludeSelfViews(e.target.checked);
                setPage(1);
              }}
            />
            Include self-views of emergency contact
          </label>
        </div>
      </Card>

      {error && (
        <Card>
          <div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{error}</div>
        </Card>
      )}

      {loading && (
        <div
          aria-hidden="true"
          style={{
            height: 320,
            background: C.paperWarm,
            borderRadius: 18,
            border: `1px solid ${C.hairlineLt}`,
          }}
        />
      )}

      {!loading && rows && rows.length === 0 && (
        <Card>
          <div
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 15,
              color: C.inkMute,
              textAlign: "center",
              padding: "16px 0",
            }}
          >
            No audit events match the current filter.
          </div>
        </Card>
      )}

      {!loading && rows && rows.length > 0 && (
        <>
          <div
            style={{
              fontFamily: F.body,
              fontSize: 12,
              color: C.inkSoft,
              fontWeight: 700,
            }}
          >
            {total} event{total === 1 ? "" : "s"} · page {page} of {totalPages}
          </div>
          <Card padded={false}>
            {rows.map((r, i) => (
              <AuditRowView
                key={r.id}
                row={r}
                isFirst={i === 0}
                expanded={expanded.has(r.id)}
                onToggle={() => toggle(r.id)}
              />
            ))}
          </Card>
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              ← Previous
            </Button>
            <div style={{ flex: 1 }} />
            <Button
              variant="ghost"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next →
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function AuditRowView({
  row,
  isFirst,
  expanded,
  onToggle,
}: {
  row: AuditRow;
  isFirst: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const actorLabel = row.actor.first && row.actor.last
    ? `${row.actor.first} ${row.actor.last}`
    : row.actor.upn || "(unknown)";
  const subjectLabel = row.subject
    ? `${row.subject.first ?? ""} ${row.subject.last ?? ""}`.trim() || row.subject.email || `user ${row.subject.id}`
    : null;
  const ts = formatTs(row.ts);

  return (
    <div
      style={{
        padding: "12px 16px",
        borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "pointer",
        }}
        onClick={onToggle}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: F.mono,
              fontSize: 11,
              color: C.inkMute,
              fontWeight: 700,
              letterSpacing: 0.4,
            }}
          >
            {ts}
          </div>
          <div
            style={{
              fontFamily: F.body,
              fontSize: 14,
              fontWeight: 700,
              color: C.ink,
              marginTop: 2,
              lineHeight: 1.4,
            }}
          >
            <span style={{ color: C.brassDk }}>{actorLabel}</span>
            <span style={{ color: C.inkSoft, fontWeight: 600 }}> · </span>
            <span style={{ fontFamily: F.mono, fontSize: 12 }}>{row.action}</span>
            {subjectLabel && (
              <>
                <span style={{ color: C.inkSoft, fontWeight: 600 }}> → </span>
                <span style={{ color: C.heatherDk }}>{subjectLabel}</span>
              </>
            )}
          </div>
        </div>
        <div
          style={{
            fontFamily: F.mono,
            fontSize: 11,
            color: C.inkMute,
            fontWeight: 700,
          }}
        >
          {expanded ? "▾" : "▸"}
        </div>
      </div>
      {expanded && (
        <pre
          style={{
            margin: "10px 0 0",
            padding: 10,
            borderRadius: 10,
            background: C.paperWarm,
            border: `1px solid ${C.hairlineLt}`,
            fontFamily: F.mono,
            fontSize: 11,
            color: C.ink,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          {JSON.stringify(
            {
              payload: row.payload,
              resource: row.resourceType ? `${row.resourceType}#${row.resourceId}` : null,
              ip: row.ip,
              isSelfView: row.isSelfView,
            },
            null,
            2,
          )}
        </pre>
      )}
    </div>
  );
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}
